"use client";

import { CheckCircle, Mail, MapPin, Phone, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  customerPaidProcessingFee,
  resolveProductOrderAmountPaid,
} from "@/lib/productOrderAmounts";
import { formatPrice, type CurrencyDisplay } from "@/lib/utils";
import type { BookingProduct } from "@/types/booking";

type ProductOrderSuccessProps = {
  invoiceNumber?: string | null;
  products: BookingProduct[];
  buyer: {
    firstName: string;
    lastName: string;
    email: string;
    callingCode: string;
    phone: string;
  };
  businessInfo?: { name?: string | null } | null;
  selectedSalon?: {
    address?: string | null;
    city?: string | null;
    state?: string | null;
    phone?: string | null;
    callingCode?: string | null;
  } | null;
  currency: CurrencyDisplay;
  subtotal: number;
  taxAmount: number;
  processingFee: number | null;
  totalPaid: number;
  onContinueShopping: () => void;
};

function formatSalonLocation(
  salon?: ProductOrderSuccessProps["selectedSalon"],
): string {
  const address = salon?.address?.trim() || "";
  const city = salon?.city?.trim() || "";
  const state = salon?.state?.trim() || "";
  const haystack = address.toLowerCase();
  const parts = [address];
  if (city && !haystack.includes(city.toLowerCase())) parts.push(city);
  if (
    state &&
    !haystack.includes(state.toLowerCase()) &&
    state.toLowerCase() !== city.toLowerCase()
  ) {
    parts.push(state);
  }
  return parts.filter(Boolean).join(", ");
}

export default function ProductOrderSuccess({
  invoiceNumber,
  products,
  buyer,
  businessInfo,
  selectedSalon,
  currency,
  subtotal,
  taxAmount,
  processingFee,
  totalPaid,
  onContinueShopping,
}: ProductOrderSuccessProps) {
  const goodsTotal = subtotal + taxAmount;
  const displayTotalPaid = resolveProductOrderAmountPaid({
    amountPaid: totalPaid,
    goodsTotal,
  });
  const processingFeeAmount = customerPaidProcessingFee({
    processingFee,
    amountPaid: displayTotalPaid,
    goodsTotal,
  });

  return (
    <div className="min-h-screen bg-grey-5">
      <div className="bg-green-50 border-b border-green-200">
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-green-300/10 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-grey-900 mb-2">
            Order Confirmed!
          </h1>
          <p className="text-grey-600">
            Your order has been successfully placed
          </p>
          {invoiceNumber ? (
            <p className="text-sm text-grey-500 mt-2">
              Order #: {invoiceNumber}
            </p>
          ) : null}
          {buyer.email ? (
            <p className="text-sm text-grey-500 mt-1">
              We&apos;ve sent a confirmation email to {buyer.email}
            </p>
          ) : null}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Order Details
          </h2>
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-grey-400 mt-0.5" />
            <div>
              <p className="font-medium text-grey-900">{businessInfo?.name}</p>
              <p className="text-sm text-grey-600">
                {formatSalonLocation(selectedSalon)}
              </p>
              {selectedSalon?.phone ? (
                <p className="text-sm text-grey-600">
                  Tel: {selectedSalon.callingCode || "+234"}{" "}
                  {selectedSalon.phone}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">Items</h2>
          <div className="space-y-3">
            {products.map((product) => (
              <div
                key={product.id}
                className="flex justify-between items-start pb-3 border-b border-grey-100 last:border-0"
              >
                <div className="flex-1">
                  <p className="font-medium text-grey-900">
                    {product.name}
                    {(product.quantity || 1) > 1
                      ? ` x${product.quantity}`
                      : ""}
                  </p>
                  <p className="text-sm text-grey-600 mt-1">Product</p>
                </div>
                <p className="font-medium text-grey-900">
                  {formatPrice(
                    (product.retailPrice || 0) * (product.quantity || 1),
                    currency,
                  )}
                </p>
              </div>
            ))}

            <div className="flex justify-between items-center pt-3 border-t border-grey-100">
              <span className="text-sm text-grey-600">Subtotal</span>
              <span className="text-sm text-grey-900">
                {formatPrice(subtotal, currency)}
              </span>
            </div>
            {taxAmount > 0 ? (
              <div className="flex justify-between items-center">
                <span className="text-sm text-grey-600">Tax</span>
                <span className="text-sm text-grey-900">
                  {formatPrice(taxAmount, currency)}
                </span>
              </div>
            ) : null}
            {processingFeeAmount > 0 ? (
              <div className="flex justify-between items-center">
                <span className="text-sm text-grey-600">Processing fee</span>
                <span className="text-sm text-grey-900">
                  {formatPrice(processingFeeAmount, currency)}
                </span>
              </div>
            ) : null}
            <div className="flex justify-between items-center pt-3">
              <span className="text-lg font-semibold text-grey-900">
                Total Paid
              </span>
              <span className="text-xl font-bold text-green-600">
                {formatPrice(displayTotalPaid, currency)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Customer Information
          </h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-grey-400" />
              <div>
                <p className="text-sm text-grey-600">Name</p>
                <p className="font-medium text-grey-900">
                  {buyer.firstName} {buyer.lastName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-grey-400" />
              <div>
                <p className="text-sm text-grey-600">Email</p>
                <p className="font-medium text-grey-900">{buyer.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-grey-400" />
              <div>
                <p className="text-sm text-grey-600">Phone</p>
                <p className="font-medium text-grey-900">
                  {buyer.callingCode} {buyer.phone}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={onContinueShopping}
            className="flex-1 bg-green-300 hover:bg-green-400 text-white"
          >
            Continue Shopping
          </Button>
          <Button
            onClick={() => window.print()}
            variant="outline"
            className="flex-1 border-grey-300"
          >
            Print Confirmation
          </Button>
        </div>
      </div>
    </div>
  );
}
